/* eslint-disable react/prop-types */
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@egaranti/components";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import CheckRole from "../shared/checkRole";

import { hasPermission } from "@/lib/employeePermissions";

import SubMerchantService from "@/services/subMerchantService";

import useAppStore from "@/stores/useAppStore";
import useAuthStore from "@/stores/useAuthStore";

import {
  Home,
  Package,
  Users,
  Shield,
  Disc,
  LayoutGrid,
  Settings,
  Store,
  LogOut,
  ExternalLink,
  ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";

const Navbar = ({ collapsed = false }) => {
  const { t } = useTranslation();
  const { logout, user } = useAuthStore();
  const { setOpenMobileMenu, setSidebarCollapsed } = useAppStore();
  const [openSubNavKey, setOpenSubNavKey] = useState(null);
  const [hasSubMerchants, setHasSubMerchants] = useState(false);

  // EPS-5027: alt bayi listesi yalnızca ana bayinin admin kullanıcısı için anlamlı.
  useEffect(() => {
    if (user?.type !== "ADMIN") return;

    SubMerchantService.getSubMerchants()
      .then((subMerchants) => setHasSubMerchants(subMerchants.length > 0))
      .catch(() => setHasSubMerchants(false));
  }, [user?.type]);

  const handleSubNavToggle = (key) => {
    if (collapsed) {
      setSidebarCollapsed(false);
      setOpenSubNavKey(key);
    } else {
      setOpenSubNavKey(openSubNavKey === key ? null : key);
    }
  };

  const userCountry = localStorage.getItem("userCountry");

  const handleNavItemClick = () => {
    setOpenMobileMenu(false);
  };

  const menuItems = [
    {
      name: t("navbar.menuItems.overview"),
      key: "OVERVIEW",
      icon: Home,
      link: "/",
    },
    // EPS-5027: yalnızca kendisine bağlı alt bayisi olan ana bayide görünür.
    ...(hasSubMerchants
      ? [
          {
            name: t("subMerchantsPage.menuTitle", "Alt Bayiler"),
            key: "SUB_MERCHANTS",
            icon: Store,
            link: "/sub-merchants",
          },
        ]
      : []),
    {
      name: t("navbar.menuItems.products"),
      key: "PRODUCTS",
      icon: Package,
      openSubNav: true,
      subMenuItems: [
        {
          name: t("navbar.menuItems.products"),
          key: "PRODUCTS_LIST",
          link: "/products/products",
        },
        {
          name: t("navbar.menuItems.integrations"),
          key: "PRODUCTS_INTEGRATIONS",
          link: "/products/integrations",
        },
      ],
    },
    {
      name: t("navbar.menuItems.clients"),
      key: "CLIENTS",
      icon: Users,
      subMenuItems: [
        {
          name: t("navbar.menuItems.individual"),
          key: "CLIENTS_INDIVIDUAL",
          link: "/customers/individual",
        },
        {
          name: t("navbar.menuItems.corporate"),
          key: "CLIENTS_CORPORATE",
          link: "/customers/corporate",
        },
      ],
    },
    ...(userCountry !== "UZ"
      ? [
          {
            name: t("navbar.menuItems.extraWarranty"),
            key: "ACTIVATIONS",
            icon: Shield,
            openSubNav: true,
            subMenuItems: [
              {
                name: t("navbar.menuItems.sales"),
                key: "ACTIVATIONS_SALES",
                link: "/extended-warranties/transactions",
              },
              {
                // EPS-4976: Adaylar ayrı yetki kırılımı değil, Satışlar yetkisiyle açılır.
                name: t("navbar.menuItems.candidates", "Adaylar"),
                key: "ACTIVATIONS_SALES",
                link: "/extended-warranties/candidates",
              },
              {
                name: t("navbar.menuItems.packages"),
                key: "ACTIVATIONS_PACKAGES",
                link: "/extended-warranties/packages",
              },
              {
                name: t("navbar.menuItems.saleReports", "Raporlar"),
                key: "ACTIVATIONS_REPORTS",
                link: "/extended-warranties/sale-reports",
              },
            ],
          },
        ]
      : []),
    {
      name: t("navbar.menuItems.campaigns"),
      key: "CAMPAIGNS",
      icon: Disc,
      link: "/campaigns",
    },
    {
      name: t("navbar.menuItems.applications"),
      key: "APPS",
      icon: LayoutGrid,
      link: "/applications",
    },
    {
      name: t("navbar.menuItems.settings"),
      key: "SETTINGS",
      icon: Settings,
      link: "/settings",
    },
  ];

  // Alt kırılım yetkisi olmayan sayfalar menüden düşer; hiçbir alt kırılıma
  // yetkisi kalmayan üst menü de gösterilmez.
  const visibleMenuItems = menuItems
    .map((item) =>
      item.subMenuItems
        ? {
            ...item,
            subMenuItems: item.subMenuItems.filter((subItem) =>
              hasPermission(user?.roles, subItem.key, "VIEW"),
            ),
          }
        : item,
    )
    .filter((item) => !item.subMenuItems || item.subMenuItems.length > 0);

  return (
    <TooltipProvider delayDuration={0}>
      <nav className="flex flex-col gap-1 px-3 pb-4 pt-6">
        {visibleMenuItems.map((item, index) => (
          <CheckRole key={item.name} action="VIEW" section={item.key}>
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.03, duration: 0.2 }}
            >
              <NavItem
                link={item.link}
                hasSubNav={item.subMenuItems}
                isOpen={openSubNavKey === item.key}
                onToggle={() => handleSubNavToggle(item.key)}
                onClick={handleNavItemClick}
                isExternal={item.isExternal}
                collapsed={collapsed}
                Icon={item.icon}
                name={item.name}
              />
            </motion.div>
          </CheckRole>
        ))}

        {/* Logout Button */}
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: visibleMenuItems.length * 0.03, duration: 0.2 }}
          className="pt-4"
        >
          <AlertDialog>
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertDialogTrigger asChild>
                  <motion.div
                    role="button"
                    tabIndex="0"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    className={cn(
                      "group flex cursor-pointer items-center rounded-xl px-3 py-2.5 text-[#86868b] transition-all duration-200",
                      "hover:bg-red-50 hover:text-[#ff3b30]",
                      collapsed ? "justify-center" : "gap-3"
                    )}
                  >
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-black/[0.04] transition-all group-hover:bg-red-50 group-hover:ring-red-200">
                      <LogOut size={18} strokeWidth={1.5} className="text-[#86868b] group-hover:text-[#ff3b30]" />
                    </div>
                    <AnimatePresence>
                      {!collapsed && (
                        <motion.span
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: "auto" }}
                          exit={{ opacity: 0, width: 0 }}
                          className="text-[13px] font-medium tracking-[-0.01em]"
                        >
                          {t("navbar.menuItems.logout")}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </AlertDialogTrigger>
              </TooltipTrigger>
              {collapsed && (
                <TooltipContent side="right" className="bg-[#1d1d1f] text-white">
                  {t("navbar.menuItems.logout")}
                </TooltipContent>
              )}
            </Tooltip>
            <AlertDialogContent className="rounded-2xl border-0 bg-white shadow-2xl">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-[17px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">
                  {t("navbar.menuItems.warning")}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-[15px] text-[#86868b]">
                  {t("navbar.menuItems.logoutWarning")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="gap-2 sm:gap-2">
                <AlertDialogCancel className="rounded-xl border-0 bg-[#f5f5f7] px-5 text-[15px] font-medium text-[#1d1d1f] hover:bg-[#e5e5e7]">
                  {t("navbar.menuItems.cancel")}
                </AlertDialogCancel>
                <Button
                  onClick={logout}
                  className="rounded-xl bg-red-600 px-5 text-[15px] font-medium hover:bg-red-700"
                >
                  {t("navbar.menuItems.logout")}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </motion.div>
      </nav>
    </TooltipProvider>
  );
};

const NavItem = ({
  hasSubNav,
  link,
  isOpen,
  onToggle,
  onClick,
  isExternal,
  collapsed,
  Icon,
  name,
}) => {
  const location = useLocation();

  const isActive = (url) => {
    if (!url) return false;
    return location.pathname === url;
  };

  const isSubItemActive = hasSubNav?.some(
    (subItem) => location.pathname === subItem.link
  );

  const itemContent = (isActiveState) => (
    <div
      className={cn(
        "group flex items-center rounded-xl px-3 py-2.5 transition-all duration-200",
        collapsed ? "justify-center" : "gap-3",
        isActiveState
          ? "bg-[#E6EEFB] text-[#0049E6]"
          : "text-[#1d1d1f] hover:bg-white/80"
      )}
    >
      <div
        className={cn(
          "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-all",
          isActiveState
            ? "bg-[#0049E6] text-white shadow-sm"
            : "bg-white shadow-sm ring-1 ring-black/[0.04] group-hover:ring-black/[0.08]"
        )}
      >
        <Icon size={18} strokeWidth={1.5} className={isActiveState ? "text-white" : "text-[#1d1d1f]"} />
      </div>
      <AnimatePresence>
        {!collapsed && (
          <motion.span
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: "auto" }}
            exit={{ opacity: 0, width: 0 }}
            className="text-[13px] font-medium tracking-[-0.01em]"
          >
            {name}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );

  if (hasSubNav) {
    return (
      <div className="flex flex-col">
        <Tooltip>
          <TooltipTrigger asChild>
            <motion.div
              role="button"
              tabIndex="0"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className={cn(
                "group flex cursor-pointer items-center rounded-xl px-3 py-2.5 transition-all duration-200",
                collapsed ? "justify-center" : "justify-between",
                isSubItemActive
                  ? "bg-[#E6EEFB] text-[#0049E6]"
                  : "text-[#1d1d1f] hover:bg-white/80"
              )}
              onClick={onToggle}
            >
              <div className={cn("flex items-center", collapsed ? "" : "gap-3")}>
                <div
                  className={cn(
                    "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-all",
                    isSubItemActive
                      ? "bg-[#0049E6] text-white shadow-sm"
                      : "bg-white shadow-sm ring-1 ring-black/[0.04] group-hover:ring-black/[0.08]"
                  )}
                >
                  <Icon size={18} strokeWidth={1.5} className={isSubItemActive ? "text-white" : "text-[#1d1d1f]"} />
                </div>
                <AnimatePresence>
                  {!collapsed && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: "auto" }}
                      exit={{ opacity: 0, width: 0 }}
                      className="text-[13px] font-medium tracking-[-0.01em]"
                    >
                      {name}
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
              {!collapsed && (
                <motion.div
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                >
                  <ChevronDown size={14} className="text-[#86868b]" />
                </motion.div>
              )}
            </motion.div>
          </TooltipTrigger>
          {collapsed && (
            <TooltipContent side="right" className="bg-[#1d1d1f] text-white">
              {name}
            </TooltipContent>
          )}
        </Tooltip>

        <AnimatePresence>
          {isOpen && !collapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="overflow-hidden"
            >
              <div className="ml-[22px] mt-1 flex flex-col gap-0.5 border-l border-[#e5e5e7] pl-4">
                {hasSubNav.map((subItem, index) =>
                  subItem.isExternal ? (
                    <motion.a
                      key={subItem.name}
                      initial={{ opacity: 0, x: -5 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      href={subItem.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={onClick}
                      className="flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] font-medium text-[#86868b] transition-colors hover:bg-white/80 hover:text-[#1d1d1f]"
                    >
                      {subItem.name}
                      <ExternalLink size={12} strokeWidth={1.5} />
                    </motion.a>
                  ) : (
                    <motion.div
                      key={subItem.name}
                      initial={{ opacity: 0, x: -5 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <NavLink
                        to={subItem.link}
                        onClick={onClick}
                        className={({ isActive }) =>
                          cn(
                            "block rounded-lg px-3 py-2 text-[13px] font-medium transition-all",
                            isActive
                              ? "bg-[#E6EEFB] text-[#0049E6]"
                              : "text-[#86868b] hover:bg-white/80 hover:text-[#1d1d1f]"
                          )
                        }
                      >
                        {subItem.name}
                      </NavLink>
                    </motion.div>
                  )
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  if (isExternal) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.a
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClick}
            className={cn(
              "group flex items-center rounded-xl px-3 py-2.5 text-[#1d1d1f] transition-all duration-200 hover:bg-white/80",
              collapsed ? "justify-center" : "gap-3"
            )}
          >
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-black/[0.04] transition-all group-hover:ring-black/[0.08]">
              <Icon size={18} strokeWidth={1.5} className="text-[#1d1d1f]" />
            </div>
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  className="flex items-center gap-2 text-[13px] font-medium tracking-[-0.01em]"
                >
                  {name}
                  <ExternalLink size={12} strokeWidth={1.5} className="text-[#86868b]" />
                </motion.span>
              )}
            </AnimatePresence>
          </motion.a>
        </TooltipTrigger>
        {collapsed && (
          <TooltipContent side="right" className="bg-[#1d1d1f] text-white">
            {name}
          </TooltipContent>
        )}
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <NavLink to={link} onClick={onClick}>
          {({ isActive: linkIsActive }) => (
            <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
              {itemContent(isActive(link) || linkIsActive)}
            </motion.div>
          )}
        </NavLink>
      </TooltipTrigger>
      {collapsed && (
        <TooltipContent side="right" className="bg-[#1d1d1f] text-white">
          {name}
        </TooltipContent>
      )}
    </Tooltip>
  );
};

export default Navbar;
